export type BadgeStatus = 'auto' | 'edited' | 'added';

export interface Badge {
  readonly id: number;
  x: number;
  y: number;
  note: string;
  string: number | null;
  fret: number | null;
  status: BadgeStatus;
  beatIndex: number | null;
  noteIndex: number | null;
  page: number;
  // issue #166: 繰り返し伝播で自動変更された (ユーザ未確認)。再編集時に確認モーダルを出す契機。
  propagated?: boolean;
  // issue #166: 個別編集で手動所有になった。以降の伝播で上書き/削除しない。
  manualOwned?: boolean;
}

/**
 * issue #166: 完全一致で繰り返すフレーズの検出結果 (backend が JSON に付与)。
 * occurrences は各出現の beat index 昇順リスト。全 occurrence 同一長で、
 * occurrence[j][o] と occurrence[k][o] が対応 (同じ beat 数目)。
 */
export interface RepeatGroup {
  id: string;
  occurrences: number[][];
}

/** issue #166: 繰り返し編集確認モーダルへ渡す保留中の運指変更内容。 */
export interface RepeatEditUpdates {
  string: number | null;
  fret: number | null;
  note: string;
  status: BadgeStatus;
}

/** issue #166: 自動変更バッジの再編集で保留される「個別 or 全体」確認の対象。 */
export interface RepeatEditConfirm {
  badgeId: number;
  updates: RepeatEditUpdates;
}

export interface FingeringNote {
  pitch: string;
  string: number;
  fret: number;
  finger?: number | null;
}

export interface FingeringBeat {
  measure: number;
  x: number;
  y_bottom: number;
  page: number;
  notes: FingeringNote[];
  position: number;
  duration?: number;
}

export interface FingeringMeta {
  title: string;
  part: string;
  scordatura?: string | null;
  composer?: string;
  arranger?: string;
  key?: string;
}

export interface FingeringData {
  meta: FingeringMeta;
  beats: FingeringBeat[];
  // issue #166: backend が付与する繰り返しフレーズのメタ情報 (無ければ未検出 / 旧データ)。
  repeat_groups?: RepeatGroup[];
}

export interface StatusColorConfig {
  bg: string;
  border: string;
  label: string;
}

export const STATUS_COLORS: Record<BadgeStatus, StatusColorConfig> = {
  auto:   { bg: '#3b82f6', border: '#2563eb', label: '自動生成' },
  edited: { bg: '#10b981', border: '#059669', label: '手動修正済' },
  added:  { bg: '#10b981', border: '#059669', label: '新規追加' },
};

export const CIRCLE_NUMS = ['①', '②', '③', '④', '⑤', '⑥'] as const;

// BadgeSvg 中央表示用の通常数字 (H4: 色枠+色字 描画と組み合わせる)。
// score-engine annotate_pdf.py の STRING_DIGIT と同じ意図 (WYSIWYG 一致)。
export const STRING_DIGITS = ['1', '2', '3', '4', '5', '6'] as const;

export const STRING_COLORS: Record<number, string> = {
  1: '#990099',
  2: '#0000cc',
  3: '#008c00',
  4: '#bf7300',
  5: '#8c0000',
  6: '#595959',
};

export const STRING_COLORS_LIGHT: Record<number, string> = {
  1: '#cc44cc',
  2: '#5588ff',
  3: '#44cc44',
  4: '#ffaa33',
  5: '#ee5555',
  6: '#99aaaa',
};

// 弦番号が未確定 (string == null) の音に使う既定色。
// BadgeSvg / ChordNotePicker で共有する。
export const DEFAULT_NOTE_COLOR = '#660099';

// 弦番号 → 表示色。未確定 (null) / 未定義弦は DEFAULT_NOTE_COLOR。
// BadgeSvg / PlacementGhost が共有する (STRING_COLORS の唯一の参照口)。
export function noteColor(s: number | null): string {
  return s ? (STRING_COLORS[s] ?? DEFAULT_NOTE_COLOR) : DEFAULT_NOTE_COLOR;
}

export interface DragState {
  badgeId: number;
  beatIndex: number | null;
  startX: number;
  startY: number;
  currentX: number;
  currentY: number;
  phase: 'pending' | 'dragging';
  startClientX: number;
  startClientY: number;
  // drag 開始時の pointer SVG 座標 (transform 方式で使う drag delta 算出起点)
  startSvgX: number;
  startSvgY: number;
}

export type EditorMode = 'edit' | 'move';
